import React, { useState, useEffect, useMemo, useRef } from "react";
import { dataClient as base44 } from "@/services/data";
import { format } from "date-fns";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import TopBar from "@/components/organogram/TopBar";
import BoardSection from "@/components/organogram/BoardSection";
import DivisionCard from "@/components/organogram/DivisionCard";
import NodeEditModal from "@/components/organogram/NodeEditModal";
import PersonDetailModal from "@/components/organogram/PersonDetailModal";
import EditToolbar from "@/components/organogram/EditToolbar";
import Footer from "@/components/organogram/Footer";
import ReorderContainer from "@/components/organogram/ReorderContainer";
import HierarchyModal from "@/components/organogram/HierarchyModal";
import StatsModal from "@/components/organogram/StatsModal";
import AuditLogModal from "@/components/organogram/AuditLogModal";
import CategoryBadgeColorPicker from "@/components/organogram/CategoryBadgeColorPicker";
import OrgExportView from "@/components/organogram/OrgExportView";
import { useTheme } from "@/components/ThemeProvider";
import { useAuth } from "@/lib/AuthContext";
import { hexToRgba, getBadgeColor, setBadgeColor, getBgColor, setBgColor, getBoardColor, setBoardColor, getFooterColor, setFooterColor, getDividerColor, setDividerColor, parseBadgeColors } from "@/components/organogram/seliseColors";

export default function Organogram() {
  const { theme } = useTheme();
  const { user, logout } = useAuth();
  const isAdmin = user?.role === "admin";
  const [nodes, setNodes] = useState([]);
  const [themeConfig, setThemeConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [editingNode, setEditingNode] = useState(null);
  const [viewingPerson, setViewingPerson] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showHierarchy, setShowHierarchy] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [showAuditLog, setShowAuditLog] = useState(false);
  const exportRef = useRef(null);

  useEffect(() => {
    loadData();
  }, []);

  // Auto-enter edit mode for admins
  useEffect(() => {
    setEditMode(isAdmin);
  }, [isAdmin]);

  const loadData = async () => {
    try {
      const res = await base44.functions.invoke("getOrgData", {});
      setNodes(res.data.nodes || []);
      setThemeConfig(parseBadgeColors(res.data.themeConfig?.badge_colors));
    } catch (err) {
      console.error("Failed to load data:", err);
    }
    setLoading(false);
  };

  const handleSaveNode = async (nodeData) => {
    setSaving(true);
    try {
      console.log("Saving node:", { name: nodeData.name, is_vacancy: nodeData.is_vacancy, hasId: !!nodeData.id });
      const res = await base44.functions.invoke("manageOrgNode", {
        action: nodeData.id ? "update" : "create",
        node: nodeData,
      });
      if (res.status === 401) {
        alert("Session expired. Please log out and log back in.");
        handleLogout();
        return;
      }
      await loadData();
      setEditingNode(null);
    } catch (err) {
      console.error("Save failed:", err);
      const errorMsg = err.response?.data?.error || "Failed to save. Please try again.";
      alert(errorMsg);
      if (errorMsg.includes("log out")) {
        handleLogout();
      }
    }
    setSaving(false);
  };

  const handleDeleteNode = async (nodeId) => {
    if (!confirm("Delete this node and all its children?")) return;
    try {
      const res = await base44.functions.invoke("manageOrgNode", {
        action: "delete",
        nodeId,
      });
      if (res.status === 401) {
        alert("Session expired. Please log out and log back in.");
        handleLogout();
        return;
      }
      await loadData();
    } catch (err) {
      console.error("Delete failed:", err);
      const errorMsg = err.response?.data?.error || "Failed to delete. Please try again.";
      alert(errorMsg);
      if (errorMsg.includes("log out")) {
        handleLogout();
      }
    }
  };

  const handleReorder = async (reorderedNodes, startOrder = 0) => {
    const reorderItems = reorderedNodes.map((n, i) => ({ id: n.id, order: startOrder + i }));
    console.log("handleReorder called:", { count: reorderItems.length });
    setNodes((prev) => {
      const updates = new Map(reorderItems.map((item) => [item.id, item.order]));
      return prev.map((n) => (updates.has(n.id) ? { ...n, order: updates.get(n.id) } : n));
    });
    try {
      const res = await base44.functions.invoke("manageOrgNode", {
        action: "reorder",
        reorderItems,
      });
      console.log("Reorder response:", { status: res.status, data: res.data });
      if (res.data?.error) {
        console.error("Reorder failed:", res.data.error);
        await loadData();
      }
    } catch (err) {
      console.error("Reorder failed:", err?.response?.data || err?.message || err);
      await loadData();
    }
  };

  const handlePublish = async () => {
    if (!confirm("Publish the current organogram? This will update the 'Last published' timestamp.")) return;
    setSaving(true);
    try {
      const res = await base44.functions.invoke("publishOrg", {});
      if (res.status === 401) {
        alert("Session expired. Please log out and log back in.");
        handleLogout();
        return;
      }
      await loadData();
      alert("Organogram published successfully!");
    } catch (err) {
      console.error("Publish failed:", err);
      const errorMsg = err.response?.data?.error || "Failed to publish. Please try again.";
      alert(errorMsg);
      if (errorMsg.includes("log out") || errorMsg.includes("expired")) {
        handleLogout();
      }
    }
    setSaving(false);
  };

  const handleBadgeColorChange = async (color) => {
    const updatedConfig = setBadgeColor(themeConfig, theme, color);
    setThemeConfig(updatedConfig);
    try {
      await base44.functions.invoke("manageThemeConfig", {
        action: "update",
        badgeColors: JSON.stringify(updatedConfig),
      });
    } catch (err) {
      console.error("Failed to save badge color:", err);
    }
  };

  const handleBgColorChange = async (color) => {
    const updatedConfig = setBgColor(themeConfig, theme, color);
    setThemeConfig(updatedConfig);
    try {
      await base44.functions.invoke("manageThemeConfig", {
        action: "update",
        badgeColors: JSON.stringify(updatedConfig),
      });
    } catch (err) {
      console.error("Failed to save bg color:", err);
    }
  };

  const handleBoardColorChange = async (color) => {
    const updatedConfig = setBoardColor(themeConfig, theme, color);
    setThemeConfig(updatedConfig);
    try {
      await base44.functions.invoke("manageThemeConfig", {
        action: "update",
        badgeColors: JSON.stringify(updatedConfig),
      });
    } catch (err) {
      console.error("Failed to save board color:", err);
    }
  };

  const handleDividerColorChange = async (color) => {
    const updatedConfig = setDividerColor(themeConfig, theme, color);
    setThemeConfig(updatedConfig);
    try {
      await base44.functions.invoke("manageThemeConfig", {
        action: "update",
        badgeColors: JSON.stringify(updatedConfig),
      });
    } catch (err) {
      console.error("Failed to save divider color:", err);
    }
  };

  const handleFooterColorChange = async (color) => {
    const updatedConfig = setFooterColor(themeConfig, theme, color);
    setThemeConfig(updatedConfig);
    try {
      await base44.functions.invoke("manageThemeConfig", {
        action: "update",
        badgeColors: JSON.stringify(updatedConfig),
      });
    } catch (err) {
      console.error("Failed to save footer color:", err);
    }
  };

  const handleLogout = () => {
    logout();
  };

  const lastUpdated = useMemo(() => {
    if (nodes.length === 0) return null;
    const latest = nodes.reduce((max, n) => {
      const d = n.updated_date ? new Date(n.updated_date) : null;
      return d && (!max || d > max) ? d : max;
    }, null);
    return latest ? latest.toISOString() : null;
  }, [nodes]);

  const boardNodes = useMemo(
    () => nodes.filter((n) => n.node_type === "board").sort((a, b) => (a.order || 0) - (b.order || 0)),
    [nodes]
  );
  const delegateNode = useMemo(() => nodes.find((n) => n.node_type === "delegate"), [nodes]);
  const cSuiteNodes = useMemo(
    () => nodes.filter((n) => n.node_type === "c_suite").sort((a, b) => (a.order || 0) - (b.order || 0)),
    [nodes]
  );
  const divisionNodes = useMemo(
    () => nodes.filter((n) => n.node_type === "division").sort((a, b) => (a.order || 0) - (b.order || 0)),
    [nodes]
  );
  const allPeople = useMemo(
    () => nodes.filter((n) => n.node_type === "person"),
    [nodes]
  );

  const getPeople = (divisionId) =>
    nodes
      .filter((n) => n.parent_id === divisionId)
      .sort((a, b) => (a.order || 0) - (b.order || 0));

  const dividerColor = getDividerColor(themeConfig, theme) || "#caa326";

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <TopBar
        isAdmin={isAdmin}
        editMode={editMode}
        onToggleEdit={() => setEditMode(!editMode)}
        onLogout={handleLogout}
        onHierarchy={() => setShowHierarchy(true)}
        onStats={() => setShowStats(true)}
        onAuditLog={() => setShowAuditLog(true)}
        exportRef={exportRef}
        nodes={nodes}
      />

      <div className="max-w-[1800px] mx-auto px-6 py-8" style={{ overflow: "visible" }}>
        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">
            SELISE Group Official Organogram
          </h1>
          <p className="text-sm text-gray-500 mt-1.5 tracking-wide">
            A simple view of our people, teams, and leadership across SELISE.
          </p>
        </div>

        {/* Board Section */}
        <BoardSection
          boardNodes={boardNodes}
          delegateNode={delegateNode}
          cSuiteNodes={cSuiteNodes}
          isAdmin={editMode}
          onEditNode={setEditingNode}
          onView={setViewingPerson}
          onAddNode={(nodeType) =>
            setEditingNode({ node_type: nodeType, name: "", title: "" })
          }
          onDeleteNode={handleDeleteNode}
          onReorderBoard={(reordered) => handleReorder(reordered, 1)}
          onReorderCSuite={(reordered) => handleReorder(reordered, 0)}
          boardColor={getBoardColor(themeConfig, theme) || "#2563eb"}
          onBoardColorChange={handleBoardColorChange}
          theme={theme}
          dividerColor={dividerColor}
        />

        {/* Gold Arrow Divider */}
        {editMode && (
          <div className="flex justify-end mb-1">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 font-medium">Arrow / Line</span>
              <CategoryBadgeColorPicker
                color={getDividerColor(themeConfig, theme) || "#caa326"}
                onChange={handleDividerColorChange}
                theme={theme}
              />
            </div>
          </div>
        )}
        <div className="mb-10 flex items-center gap-1">
          <ChevronLeft className="w-4 h-4 -mr-2.5 opacity-30" style={{ color: dividerColor }} />
          <ChevronLeft className="w-5 h-5 -mr-2.5 opacity-50" style={{ color: dividerColor }} />
          <ChevronLeft className="w-6 h-6 -mr-2.5 opacity-70" style={{ color: dividerColor }} />
          <ChevronLeft className="w-7 h-7 -mr-2.5 opacity-85" style={{ color: dividerColor }} />
          <ChevronLeft className="w-8 h-8" style={{ color: dividerColor }} />
          <div className="flex-1 h-px" style={{ background: `linear-gradient(to right, ${hexToRgba(dividerColor, 0.4)}, ${dividerColor}, ${hexToRgba(dividerColor, 0.4)})` }} />
          <ChevronRight className="w-8 h-8" style={{ color: dividerColor }} />
          <ChevronRight className="w-7 h-7 -ml-2.5 opacity-85" style={{ color: dividerColor }} />
          <ChevronRight className="w-6 h-6 -ml-2.5 opacity-70" style={{ color: dividerColor }} />
          <ChevronRight className="w-5 h-5 -ml-2.5 opacity-50" style={{ color: dividerColor }} />
          <ChevronRight className="w-4 h-4 -ml-2.5 opacity-30" style={{ color: dividerColor }} />
        </div>

        {/* Business Units */}
        <div
          className="rounded-2xl p-6 border border-gray-200/60"
          style={{
            backgroundColor: getBgColor(themeConfig, theme) || undefined,
            boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 10px 30px -10px rgba(0,0,0,0.08)",
          }}
        >
          {editMode && (
            <div className="flex items-center justify-end gap-4 mb-4">
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 font-medium">Badge Color</span>
                <CategoryBadgeColorPicker
                  color={getBadgeColor(themeConfig, theme) || "#2563eb"}
                  onChange={handleBadgeColorChange}
                  theme={theme}
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 font-medium">Background</span>
                <CategoryBadgeColorPicker
                  color={getBgColor(themeConfig, theme) || "#e0efff"}
                  onChange={handleBgColorChange}
                  theme={theme}
                />
              </div>
            </div>
          )}
          {[
            { category: "industry", label: "Industry Units" },
            { category: "product", label: "Product Units" },
            { category: "service", label: "Service Units" },
            { category: "support", label: "Support Units" },
          ].map(({ category, label }) => {
            const rowDivisions = divisionNodes.filter((d) => d.category === category);
            const badgeColor = getBadgeColor(themeConfig, theme) || "#2563eb";
            return (
              <div key={category} className="mb-4 last:mb-0">
                <div className="flex items-center justify-center gap-3 mb-2 pt-4">
                  <span
                    className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold tracking-wider uppercase border"
                    style={{
                      backgroundColor: hexToRgba(badgeColor, 0.12),
                      color: badgeColor,
                      borderColor: hexToRgba(badgeColor, 0.3),
                      boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                    }}
                  >
                    {label}
                    <span
                      className="inline-flex items-center justify-center w-5 h-5 rounded-full text-white text-[10px] font-bold"
                      style={{ backgroundColor: badgeColor }}
                    >
                      {rowDivisions.length}
                    </span>
                  </span>
                  {editMode && (
                    <button
                      onClick={() =>
                        setEditingNode({
                          node_type: "division",
                          category,
                          name: "",
                          department: "",
                          department_code: "",
                        })
                      }
                      className="flex items-center gap-1 px-2 py-1 text-xs border border-dashed rounded-md transition-colors"
                      style={{
                        color: badgeColor,
                        borderColor: hexToRgba(badgeColor, 0.4),
                      }}
                    >
                      <Plus className="w-3 h-3" /> Add Unit
                    </button>
                  )}
                </div>
                <div className="overflow-x-auto pt-4 pb-2">
                  <ReorderContainer
                    items={rowDivisions}
                    onReorder={(reordered) => handleReorder(reordered, 0)}
                    droppableId={`divisions-${category}`}
                    direction="horizontal"
                    disabled={!editMode}
                    className="inline-flex gap-4 justify-center min-w-full items-stretch"
                    renderItem={(d, index, dragProvided) => (
                      <div
                        ref={dragProvided?.innerRef}
                        {...dragProvided?.draggableProps}
                        {...dragProvided?.dragHandleProps}
                        className={`self-stretch ${editMode ? "cursor-grab active:cursor-grabbing" : ""}`}
                      >
                        <DivisionCard
                          division={d}
                          people={getPeople(d.id)}
                          isAdmin={editMode}
                          onEditNode={setEditingNode}
                          onAddPerson={() =>
                            setEditingNode({
                              node_type: "person",
                              parent_id: d.id,
                              name: "",
                              function_area: "",
                              role_level: "Manager",
                            })
                          }
                          onDeleteNode={handleDeleteNode}
                          cSuiteNodes={cSuiteNodes}
                          allPeople={allPeople}
                          onView={setViewingPerson}
                          onReorderPeople={(reordered) => handleReorder(reordered, 0)}
                        />
                      </div>
                    )}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <Footer lastUpdated={lastUpdated} footerColor={getFooterColor(themeConfig, theme) || undefined} />
      <div ref={exportRef} style={{ position: "absolute", left: "-99999px", top: 0 }} aria-hidden="true">
        <OrgExportView nodes={nodes} theme={theme} themeConfig={themeConfig} lastUpdated={lastUpdated} />
      </div>
      {editMode && (
        <div className="fixed bottom-6 left-6 z-40 flex items-center gap-2 px-3 py-2 rounded-lg bg-white shadow-lg border border-gray-200">
          <span className="text-xs text-gray-500 font-medium">Footer</span>
          <CategoryBadgeColorPicker
            color={getFooterColor(themeConfig, theme) || "#1a1a1a"}
            onChange={handleFooterColorChange}
            theme={theme}
          />
        </div>
      )}

      {editMode && <EditToolbar onPublish={handlePublish} saving={saving} />}
      {viewingPerson && (
        <PersonDetailModal
          person={viewingPerson}
          cSuiteNodes={cSuiteNodes}
          divisionNodes={divisionNodes}
          allNodes={nodes}
          onView={setViewingPerson}
          onClose={() => setViewingPerson(null)}
        />
      )}
      {showHierarchy && (
        <HierarchyModal nodes={nodes} onClose={() => setShowHierarchy(false)} />
      )}
      {showStats && (
        <StatsModal nodes={nodes} onClose={() => setShowStats(false)} />
      )}
      {showAuditLog && (
        <AuditLogModal onClose={() => setShowAuditLog(false)} />
      )}
      {editingNode && (
        <NodeEditModal
          node={editingNode}
          onSave={handleSaveNode}
          onClose={() => setEditingNode(null)}
          saving={saving}
          cSuiteNodes={cSuiteNodes}
          allPeople={allPeople}
        />
      )}
    </div>
  );
}